/**
 * EmployeeImport – v2
 * -------------------
 * Läser Excel eller CSV, låter admin mappa kolumner, förhandsgranskar och
 * skriver till Firestore.
 *
 * Speciella virtuella fält:
 *  _firstName + _lastName  → slås ihop till displayName
 *  _aoCode                 → mappas till departmentId via departments.offerCode
 *  _email                  → matchningsnyckel (primär)
 *
 * Matchningsordning: e-post → exakt namn → (hoppar över)
 *
 * Privata/känsliga fält (ICE, hemadress, personnummer) importeras EJ
 * om inte admin explicit mappar dem.
 */
import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { db } from '../firebase/config';
import {
  collection, getDocs, doc, updateDoc, setDoc
} from 'firebase/firestore';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Stepper, Step, StepLabel,
  Table, TableHead, TableBody, TableRow, TableCell,
  Select, MenuItem, Chip, Alert, LinearProgress,
  Divider, Paper, Tooltip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const INACTIVE_STATUSES = ['Slutat', 'Pensionär'];

// ── Fält som kan ta emot data ───────────────────────────────────────────────
export const TARGET_FIELDS = [
  // Virtuella (kombineras/omvandlas)
  { key: '_email',       label: '★ E-post (matchningsnyckel)',  virtual: true },
  { key: '_firstName',   label: '★ Förnamn (del av namn)',      virtual: true },
  { key: '_lastName',    label: '★ Efternamn (del av namn)',    virtual: true },
  { key: '_aoCode',      label: '★ AO-kod → Affärsområde',     virtual: true },
  // Reella fält
  { key: 'displayName',      label: 'Namn (fullständigt)' },
  { key: 'kortnamn',         label: 'Kortnamn / smeknamn' },
  { key: 'title',            label: 'Befattning / titel (SWE)' },
  { key: 'titleEng',         label: 'Befattning / titel (ENG)' },
  { key: 'title2',           label: 'Befattning rad 2 (SWE)' },
  { key: 'title2Eng',        label: 'Befattning rad 2 (ENG)' },
  { key: 'funktion',         label: 'Funktion i företaget' },
  { key: 'mobile',           label: 'Mobiltelefon' },
  { key: 'officePhone',      label: 'Kontorstelefon' },
  { key: 'linkedin',         label: 'LinkedIn-URL' },
  { key: 'startDate',        label: 'Anställningsdatum' },
  { key: 'endDate',          label: 'Slutdatum (anställning)' },
  { key: 'employmentStatus', label: 'Anställningsstatus' },
  { key: 'location',         label: 'Ort / kontor' },
  { key: 'bio',              label: 'Presentation / bio' },
  { key: 'autodesklicens',   label: 'Autodesklicens' },
  { key: 'birthDate',        label: 'Födelsedatum' },
  { key: 'profileImageName', label: 'Profilbild (filnamn)' },
  // Privata kontaktuppgifter (för nödfall/intern kontakt)
  { key: 'homePhone',        label: 'Telefon, hem (privat)', sensitive: true },
  { key: 'privateMobile',    label: 'Mobiltelefon, privat', sensitive: true },
  { key: 'homeAddress',      label: 'Hemadress', sensitive: true },
  { key: 'homeZip',          label: 'Postnummer (hem)', sensitive: true },
  { key: 'homeCity',         label: 'Stad (hem)', sensitive: true },
  { key: 'iceContact',       label: 'Nära anhörig / ICE', sensitive: true },
];

// ── Auto-gissning på kolumnnamn (lowercase) ─────────────────────────────────
const GUESS_MAP = {
  // E-post (matchningsnyckel)
  'e-post': '_email', 'email': '_email', 'epost': '_email',
  'e-postadress': '_email', 'mail': '_email',
  // Namn-delar
  'förnamn': '_firstName', 'first name': '_firstName', 'firstname': '_firstName',
  'efternamn': '_lastName', 'last name': '_lastName', 'lastname': '_lastName',
  'title': '_lastName',  // SharePoint Title = Efternamn
  // AO
  'affärsområde': '_aoCode', 'ao': '_aoCode', 'avdelning': '_aoCode',
  'department': '_aoCode',
  // Reella fält
  'fullständigt namn': 'displayName', 'namn': 'displayName',
  'name': 'displayName', 'full name': 'displayName',
  'kortnamn': 'kortnamn', 'namnförkortning': 'kortnamn', 'smeknamn': 'kortnamn',
  'befattning': 'title', 'titel': 'title', 'roll': 'title',
  'visitkort rubrik 1 (swe)': 'title',
  'visitkort rubrik 2 (swe)': 'title2',
  'visitkort rubrik 1 (eng)': 'titleEng',
  'visitkort rubrik 2 (eng)': 'title2Eng',
  'funktion': 'funktion',
  // Telefon – "Arbetsmobil" = arbetsmobil, "Telefon, arbete" = kontorstelefon
  // OBS: "Mobiltelefon, privat" och "Telefon, Hem" auto-mappas INTE (privata)
  'arbetsmobil': 'mobile', 'mobil': 'mobile', 'mobile': 'mobile', 'cell': 'mobile',
  'telefon, arbete': 'officePhone', 'kontorstelefon': 'officePhone',
  'workphone': 'officePhone', 'office phone': 'officePhone',
  // Datum
  'anställd sedan': 'startDate', 'anställningsdatum': 'startDate',
  'start date': 'startDate', 'startdatum': 'startDate',
  'avslut': 'endDate', 'slutdatum': 'endDate', 'end date': 'endDate',
  'födelsedatum': 'birthDate', 'birthdate': 'birthDate', 'födelsedag': 'birthDate',
  // Status
  'status': 'employmentStatus',
  // Plats (arbetsort / kontor)
  'ort': 'location', 'kontor': 'location', 'location': 'location',
  // Övrigt
  'linkedin': 'linkedin',
  'presentation': 'bio', 'bio': 'bio', 'beskrivning': 'bio',
  'autodesklicens': 'autodesklicens',
  'bild': 'profileImageName', 'profilbild': 'profileImageName',
  // Privata kontaktuppgifter (hem/nödfall) – alla känsliga
  'telefon, hem': 'homePhone', 'hemtelefon': 'homePhone', 'homephone': 'homePhone',
  'mobiltelefon, privat': 'privateMobile', 'cellphone': 'privateMobile', 'privat mobil': 'privateMobile',
  'adress, hem': 'homeAddress', 'hemadress': 'homeAddress', 'workaddress': 'homeAddress',
  'postnummer': 'homeZip', 'workzip': 'homeZip', 'zip': 'homeZip',
  'stad': 'homeCity', 'workcity': 'homeCity', 'hemort': 'homeCity',
  'nära anhörig / ice': 'iceContact', 'ice': 'iceContact', 'nära anhörig': 'iceContact',
};

function autoGuess(cols) {
  const m = {};
  cols.forEach(col => {
    const key = col.toLowerCase().trim();
    if (GUESS_MAP[key]) m[col] = GUESS_MAP[key];
  });
  return m;
}

// ── Sparad mappning (localStorage) ─────────────────────────────────────────
const MAPPING_KEY = 'lagbasn_employeeImport_mapping_v1';

function saveMapping(cols, map) {
  try {
    localStorage.setItem(MAPPING_KEY, JSON.stringify({ cols, map }));
  } catch (_) {}
}

function loadSavedMapping(cols) {
  try {
    const saved = JSON.parse(localStorage.getItem(MAPPING_KEY) || 'null');
    if (!saved?.map) return null;
    // Återställ bara kolumner som faktiskt finns i den nya filen
    const restored = {};
    cols.forEach(col => { if (saved.map[col]) restored[col] = saved.map[col]; });
    return Object.keys(restored).length > 3 ? restored : null;
  } catch (_) { return null; }
}

function formatDate(val) {
  if (!val) return '';
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  const s = String(val).trim();
  // ISO datetime → date only
  if (s.match(/^\d{4}-\d{2}-\d{2}T/)) return s.slice(0, 10);
  return s;
}

// ── Komponent ───────────────────────────────────────────────────────────────
export default function EmployeeImport({ open, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [fileError, setFileError] = useState('');
  const [matchStats, setMatchStats] = useState(null);
  const [mappingRestored, setMappingRestored] = useState(false);
  // { emailMatch, nameMatch, noMatch, formerMatch, total }

  function reset() {
    setStep(0); setRows([]); setColumns([]); setMapping({});
    setPreview([]); setImporting(false); setImportResult(null);
    setFileError(''); setMatchStats(null); setMappingRestored(false);
  }
  function handleClose() { reset(); onClose(); }

  // ── Step 0: parse file ──────────────────────────────────────────────────────
  const handleFile = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    setFileError('');
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        // Auto-detektera avgränsare: semikolon eller komma
        const firstLine = evt.target.result.split('\n')[0] || '';
        const semi  = (firstLine.match(/;/g)  || []).length;
        const comma = (firstLine.match(/,/g)  || []).length;
        const delim = semi > comma ? ';' : ',';

        const wb = XLSX.read(evt.target.result, {
          type: 'binary', cellDates: true, codepage: 1252, FS: delim
        });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!data.length) { setFileError('Filen verkar tom.'); return; }
        // Rensa BOM och whitespace från kolumnnamn
        const raw = data.map(row => {
          const clean = {};
          Object.keys(row).forEach(k => {
            const cleanKey = k.replace(/^\uFEFF/, '').trim();
            clean[cleanKey] = row[k];
          });
          return clean;
        });
        const cols = Object.keys(raw[0]).filter(c =>
          c !== '' &&
          !c.startsWith('ListSchema') &&
          !c.startsWith('__EMPTY')   // trailing-semikolon skapar tomma kolumner
        );
        setColumns(cols);
        setRows(raw);
        const guessed = autoGuess(cols);
        const saved   = loadSavedMapping(cols);
        if (saved) {
          setMapping({ ...guessed, ...saved });
          setMappingRestored(true);
        } else {
          setMapping(guessed);
          setMappingRestored(false);
        }
        setStep(1);
      } catch (err) {
        console.error(err);
        setFileError('Kunde inte läsa filen. Kontrollera att det är Excel (.xlsx/.xls) eller CSV.');
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  }, []);

  // ── Step 1→2: build preview ─────────────────────────────────────────────────
  async function buildPreview() {
    // Load Firebase users + departments
    const [userSnap, deptSnap] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
    ]);
    const fbUsers = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const depts = deptSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const emailToUser = {};
    fbUsers.forEach(u => { if (u.email) emailToUser[u.email.toLowerCase()] = u; });

    const nameToUser = {};
    fbUsers.forEach(u => {
      if (u.displayName) nameToUser[u.displayName.toLowerCase()] = u;
    });

    const aoCodeToDeptId = {};
    depts.forEach(d => {
      if (d.offerCode) aoCodeToDeptId[d.offerCode.toUpperCase()] = d.id;
      // Also match by name
      aoCodeToDeptId[d.name.toUpperCase()] = d.id;
    });

    const emailCol    = Object.keys(mapping).find(c => mapping[c] === '_email');
    const firstCol    = Object.keys(mapping).find(c => mapping[c] === '_firstName');
    const lastCol     = Object.keys(mapping).find(c => mapping[c] === '_lastName');
    const aoCol       = Object.keys(mapping).find(c => mapping[c] === '_aoCode');

    let emailMatch = 0, nameMatch = 0, noMatch = 0, formerMatch = 0;

    const mapped = rows.map(row => {
      // Build fields
      const fields = {};
      Object.entries(mapping).forEach(([col, fieldKey]) => {
        if (!fieldKey || fieldKey.startsWith('_')) return;
        const val = row[col];
        if (val === undefined || val === '') return;
        const target = TARGET_FIELDS.find(f => f.key === fieldKey);
        fields[fieldKey] = target?.key?.includes('Date') || target?.key === 'startDate' || target?.key === 'endDate'
          ? formatDate(val)
          : String(val).trim();
      });

      // Virtual: combine first + last name
      if (firstCol || lastCol) {
        const first = firstCol ? String(row[firstCol] || '').trim() : '';
        const last  = lastCol  ? String(row[lastCol]  || '').trim() : '';
        if (first || last) fields.displayName = [first, last].filter(Boolean).join(' ');
      }

      // Virtual: AO-kod → departmentId
      // Fallback: "BTM-A" → "BTM" om sub-kod saknas som avdelning
      if (aoCol) {
        const raw  = String(row[aoCol] || '').trim();
        const code = raw.toUpperCase();
        let deptId = aoCodeToDeptId[code];
        if (!deptId) {
          // Försök med förkortad kod: ta bort sista -X-suffixet (t.ex. BTM-A → BTM)
          const parent = code.replace(/-[A-Z0-9]+$/, '');
          if (parent !== code) deptId = aoCodeToDeptId[parent];
        }
        if (deptId) fields.departmentId = deptId;
        fields._aoCodeRaw = raw;
      }

      // Match user
      const email = emailCol ? String(row[emailCol] || '').trim().toLowerCase() : '';
      let existing = email ? emailToUser[email] : null;
      let matchMethod = email && existing ? 'email' : null;

      if (!existing && fields.displayName) {
        existing = nameToUser[fields.displayName.toLowerCase()];
        if (existing) matchMethod = 'name';
      }

      const isFormer = INACTIVE_STATUSES.includes(fields.employmentStatus);

      if (existing && !isFormer) { matchMethod === 'email' ? emailMatch++ : nameMatch++; }
      else if (isFormer) formerMatch++;
      else noMatch++;

      // Filtrera bort artefakter från CSV-headern (t.ex. ListSchema-raden)
      if (email === 'e-post' || fields.displayName?.toLowerCase().startsWith('förnamn')) return null;
      if (!email && !fields.displayName) return null;

      return {
        email: email || fields.displayName,
        displayName: fields.displayName || existing?.displayName || '',
        fields,
        existsInFirebase: !!existing,
        userId: existing?.id,
        matchMethod,
        aoCodeRaw: fields._aoCodeRaw,
        deptFound: !!fields.departmentId,
        // isFormer gäller ALLA med inaktiv status – med eller utan konto
        isFormer,
      };
    }).filter(Boolean);

    setMatchStats({ emailMatch, nameMatch, noMatch, formerMatch, total: mapped.length });
    setPreview(mapped);
    setStep(2);
  }

  // ── Step 2→3: import ─────────────────────────────────────────────────────────
  async function runImport() {
    setImporting(true);
    let updated = 0, pending = 0, former = 0, skipped = 0;

    for (const item of preview) {
      const { _aoCodeRaw, deptFound, ...cleanFields } = item.fields;
      const updates = {
        ...cleanFields,
        // Canonical sync
        ...(cleanFields.mobile ? { signatureMobile: cleanFields.mobile } : {}),
        ...(cleanFields.officePhone ? { signatureOffice: cleanFields.officePhone } : {}),
        ...(cleanFields.linkedin ? { signaturePersonalLinkedIn: cleanFields.linkedin } : {}),
        importedAt: new Date(),
      };

      if (item.existsInFirebase && item.userId && !item.isFormer) {
        // Aktiv Firebase-användare → uppdatera profil
        await updateDoc(doc(db, 'users', item.userId), updates);
        updated++;
      } else if (item.isFormer) {
        // Former employee (Slutat/Pensionär) without login → formerEmployees
        const key = item.email.includes('@')
          ? item.email
          : item.displayName?.toLowerCase().replace(/\s+/g, '.') || 'former_' + former;
        await setDoc(doc(db, 'formerEmployees', key), {
          ...updates,
          email: item.email.includes('@') ? item.email : '',
          createdAt: new Date(),
        });
        former++;
      } else {
        // Active employee without login yet → pendingEmployees (auto-applies on first login)
        const key = item.email.includes('@')
          ? item.email
          : item.displayName?.toLowerCase().replace(/\s+/g, '.') || 'unknown_' + skipped;
        await setDoc(doc(db, 'pendingEmployees', key), {
          ...updates,
          createdAt: new Date(),
        });
        pending++;
      }
    }

    setImportResult({ updated, pending, former, skipped });
    setImporting(false);
    setStep(3);
    if (onDone) onDone();
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const hasEmailCol  = Object.values(mapping).includes('_email');
  const hasFirstName = Object.values(mapping).includes('_firstName');
  const hasLastName  = Object.values(mapping).includes('_lastName');
  const hasNameMatch = hasFirstName || hasLastName || Object.values(mapping).includes('displayName');
  const canProceed   = hasEmailCol || hasNameMatch;

  const virtualFields = TARGET_FIELDS.filter(f => f.virtual);
  const normalFields  = TARGET_FIELDS.filter(f => !f.virtual);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon sx={{ color: '#FF6B00' }} />
        Importera medarbetarregister
      </DialogTitle>

      <Box sx={{ px: 3, pt: 1 }}>
        <Stepper activeStep={step} sx={{ mb: 2 }}>
          {['Ladda upp', 'Mappa kolumner', 'Förhandsgranska', 'Klar'].map(l => (
            <Step key={l}><StepLabel>{l}</StepLabel>
          </Step>))}
        </Stepper>
      </Box>

      <DialogContent>

        {/* ── Step 0 ── */}
        {step === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Ladda upp ert medarbetarregister i Excel eller CSV.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Tips: lägg till e-postkolumnen i SharePoint-vyn för säkrast matchning.
              Annars matchas på fullständigt namn.
            </Typography>
            <Button variant="outlined" component="label"
              startIcon={<UploadFileIcon />} size="large">
              Välj fil (.xlsx, .xls, .csv)
              <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleFile} />
            </Button>
            {fileError && <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>}
          </Box>
        )}

        {/* ── Step 1: mapping ── */}
        {step === 1 && (
          <Box>
            <Alert severity={canProceed ? 'info' : 'warning'} sx={{ mb: 1 }} icon={false}>
              {rows.length} rader · {columns.length} kolumner
              {!hasEmailCol && ' · Ingen e-postkolumn — matchning sker på namn'}
              {canProceed ? '' : ' · Mappa åtminstone Förnamn+Efternamn eller Namn'}
            </Alert>
            {mappingRestored && (
              <Alert severity="success" sx={{ mb: 1 }} icon={false}>
                Mappning återställd från förra importen — kontrollera och justera vid behov.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Kolumner i filen → fält i LagbasN
              </Typography>
              <Button size="small" variant="outlined"
                onClick={() => { saveMapping(columns, mapping); }}
                sx={{ fontSize: '0.72rem' }}>
                Spara mappning
              </Button>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Kolumn</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Exempelvärde</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Mappa till</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {columns.map(col => (
                  <TableRow key={col} hover>
                    <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {col}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 180 }}>
                      <Typography variant="caption" noWrap>
                        {String(rows[0]?.[col] ?? '').slice(0, 55)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small" fullWidth
                        value={mapping[col] || ''}
                        onChange={e => setMapping(m => ({ ...m, [col]: e.target.value }))}
                        displayEmpty>
                        <MenuItem value=""><em>— ignorera —</em></MenuItem>
                        <MenuItem disabled>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            Matchning & kombination
                          </Typography>
                        </MenuItem>
                        {virtualFields.map(f => (
                          <MenuItem key={f.key} value={f.key}>
                            <strong>{f.label}</strong>
                          </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem disabled>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            Medarbetarfält
                          </Typography>
                        </MenuItem>
                        {normalFields.map(f => (
                          <MenuItem key={f.key} value={f.key}>
                            {f.sensitive
                              ? <><WarningAmberIcon sx={{ fontSize: 14, mr: 0.5, color: 'warning.main' }} />{f.label}</>
                              : f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* ── Step 2: preview ── */}
        {step === 2 && matchStats && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip icon={<CheckCircleIcon />} color="success" size="small"
                label={`${matchStats.emailMatch} matchade på e-post`} />
              <Chip icon={<CheckCircleIcon />} color="info" size="small"
                label={`${matchStats.nameMatch} matchade på namn`} />
              {matchStats.formerMatch > 0 && (
                <Chip icon={<HourglassEmptyIcon />} size="small"
                  sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a' }}
                  label={`${matchStats.formerMatch} tidigare medarbetare`} />
              )}
              {matchStats.noMatch > 0 && (
                <Chip icon={<HourglassEmptyIcon />} color="warning" size="small"
                  label={`${matchStats.noMatch} → väntande`} />
              )}
            </Box>

            {matchStats.formerMatch > 0 && (
              <Alert severity="info" sx={{ mb: 1 }} icon={false}>
                Tidigare medarbetare (Slutat/Pensionär) sparas i ett separat register — de kan ses och GDPR-raderas i medarbetarregistret.
              </Alert>
            )}
            {matchStats.noMatch > 0 && (
              <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                Väntande medarbetare auto-appliceras när de loggar in i LagbasN med sin @tikab.com-adress.
              </Alert>
            )}

            <Paper sx={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Namn</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>AO</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Arbetsmobil</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Privat mobil</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hemtelefon</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Funktion</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Åtgärd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((item, i) => (
                    <TableRow key={i} hover
                      sx={{ bgcolor: item.isFormer ? 'rgba(106,27,154,0.04)' : 'inherit' }}>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        {item.displayName}
                        {item.fields.kortnamn && (
                          <Typography component="span" sx={{ color: 'text.disabled', fontSize: '0.7rem', ml: 0.5 }}>
                            ({item.fields.kortnamn})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.aoCodeRaw && (
                          <Chip size="small" label={item.aoCodeRaw}
                            sx={{ fontSize: '0.65rem', height: 18,
                              bgcolor: item.deptFound ? '#fff3e0' : '#ffebee',
                              color: item.deptFound ? '#e65100' : '#c62828' }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                        {item.fields.mobile || <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                        {item.fields.privateMobile || <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                        {item.fields.homePhone || <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', maxWidth: 160 }}>
                        <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                          {item.fields.funktion || item.fields.title || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.isFormer
                          ? <Chip size="small"
                              label={item.fields.employmentStatus || 'Tidigare'}
                              sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontSize: '0.65rem', height: 20 }} />
                          : item.existsInFirebase
                            ? <Tooltip title={`Matchad på ${item.matchMethod}`}>
                                <Chip size="small" color="success" label="Uppdateras" />
                              </Tooltip>
                            : <Chip size="small" color="warning" label="Väntande" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* ── Step 3: done ── */}
        {step === 3 && importResult && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>Import klar!</Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
              <Chip color="success" label={`${importResult.updated} uppdaterade`} />
              {importResult.former > 0 && (
                <Chip label={`${importResult.former} tidigare medarbetare`}
                  sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a' }} />
              )}
              {importResult.pending > 0 && (
                <Chip color="warning" label={`${importResult.pending} väntande`} />
              )}
            </Box>
          </Box>
        )}

        {importing && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{step === 3 ? 'Stäng' : 'Avbryt'}</Button>
        {step === 1 && (
          <Button variant="contained" disabled={!canProceed} onClick={buildPreview}>
            Förhandsgranska →
          </Button>
        )}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)}>← Tillbaka</Button>
            <Button variant="contained" disabled={importing || !preview.length}
              onClick={runImport}>
              Importera {preview.length} rader
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
